import type { FormValues } from "@/components/booking-form/types";
import {
  CH_MEMBERSHIP_TERMS_VERSION,
  CH_TERMS_VERSION,
  type MarketCountry,
} from "@/config/market";

/**
 * What a booking records in `bookings.country_specific_consent`, one builder
 * per country. The column is jsonb; every entry carries at least
 * { name, acceptedAt }, and the name is country-prefixed so the entries of one
 * market can never be mistaken for another's.
 *
 * Timestamps are the moment the box was TICKED (captured by
 * MedicalConsentField), not the submit. A restored booking that lacks the
 * click time falls back to `now`, the same way it always has for Germany.
 */

type TimestampedConsent = { name: string; acceptedAt: string };

export type ChGtcConsent = TimestampedConsent & {
  name: "ch_gtc_consent";
  consentVariant: NonNullable<FormValues["chGtcConsentVariant"]>;
  termsVersion: string;
  membershipTermsVersion: string | null;
  locale: string;
  labelText: string | null;
};

export type CountrySpecificConsent = TimestampedConsent[] | [ChGtcConsent];

const ticked = (
  name: string,
  isTicked: boolean | undefined,
  tickedAt: string | undefined,
  now: Date,
): TimestampedConsent | null =>
  isTicked ? { name, acceptedAt: tickedAt ?? now.toISOString() } : null;

/** Germany: the three private-treatment consents (§1(2) GOÄ Wunschleistung,
 *  §630a BGB, §203 StGB), each its own legal act with its own timestamp. */
export const germanConsents = (
  formData: FormValues,
  now: Date,
): TimestampedConsent[] =>
  [
    ticked(
      "de_private_treatment_request_consent",
      formData.deWunschleistungConsent,
      formData.deWunschleistungConsentAt,
      now,
    ),
    ticked(
      "de_partner_practice_treatment_consent",
      formData.deTreatmentConsent,
      formData.deTreatmentConsentAt,
      now,
    ),
    ticked(
      "de_medical_confidentiality_waiver",
      formData.deConfidentialityConsent,
      formData.deConfidentialityConsentAt,
      now,
    ),
  ].filter((c): c is TimestampedConsent => c !== null);

/** Austria: the lab and partner-doctor contract, the release from medical
 *  confidentiality (§ 54 ÄrzteG 1998) and the early-performance withdrawal
 *  waiver. Separate entries on purpose: each is a distinct legal act. */
export const austrianConsents = (
  formData: FormValues,
  now: Date,
): TimestampedConsent[] =>
  [
    ticked(
      "at_lab_and_partner_doctor_contract_consent",
      formData.atLabAndDoctorConsent,
      formData.atLabAndDoctorConsentAt,
      now,
    ),
    ticked(
      "at_medical_confidentiality_release",
      formData.atConfidentialityConsent,
      formData.atConfidentialityConsentAt,
      now,
    ),
    ticked(
      "at_early_performance_withdrawal_waiver",
      formData.atWithdrawalConsent,
      formData.atWithdrawalConsentAt,
      now,
    ),
  ].filter((c): c is TimestampedConsent => c !== null);

/**
 * Switzerland (AHD-12757): one record for the T&C tick. It carries more than
 * a timestamp because the label is conditional: with a membership in the cart
 * the sentence also names the Membership T&Cs, which the general T&Cs never
 * incorporate. Variant, both version ids, the locale served and the rendered
 * label are the evidence a separate checkbox would otherwise have given. All
 * of it was captured at the tick and cleared whenever the membership flag
 * flipped, so this is the sentence that was on screen when the box was
 * ticked, not what the cart looks like at submit.
 */
export const swissGtcConsent = (
  formData: FormValues,
  language: string,
  now: Date,
): ChGtcConsent | null => {
  if (!formData.tosAndPpConsent) return null;
  const consentVariant = formData.chGtcConsentVariant ?? "gtc_only";
  return {
    name: "ch_gtc_consent",
    acceptedAt: formData.chGtcConsentAt ?? now.toISOString(),
    consentVariant,
    termsVersion: CH_TERMS_VERSION,
    membershipTermsVersion:
      consentVariant === "gtc_membership" ? CH_MEMBERSHIP_TERMS_VERSION : null,
    locale: language,
    labelText: formData.chGtcConsentLabel ?? null,
  };
};

/**
 * The value for `country_specific_consent`, or undefined when this market and
 * situation record nothing, so the caller omits the key instead of sending an
 * empty one. Internal bookings record no Swiss consent, mirroring the other
 * consent dates the internal flow nulls; Germany and Austria keep writing
 * their (possibly empty) set, unchanged from before this module existed.
 */
export const countrySpecificConsent = ({
  market,
  formData,
  language,
  isInternal,
  now = new Date(),
}: {
  market: MarketCountry;
  formData: FormValues;
  language: string;
  isInternal: boolean;
  now?: Date;
}): CountrySpecificConsent | undefined => {
  if (market === "de") return germanConsents(formData, now);
  if (market === "at") return austrianConsents(formData, now);
  if (market === "ch" && !isInternal) {
    const record = swissGtcConsent(formData, language, now);
    return record ? [record] : undefined;
  }
  return undefined;
};
